import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, Loader2, Star, Play } from "lucide-react";
import { Slideshow } from "@/components/community/Slideshow";
import { ShareAlbum } from "@/components/community/ShareAlbum";
import { startGoogleLogin } from "@/context/CustomerAuthContext";
import { toast } from "sonner";
import CommunityLayout from "@/components/community/CommunityLayout";
import { SectionTitle, EmptyState } from "@/components/community/Cards";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cApi, img, fmtDate, errMsg, useCommunityMe } from "@/lib/community";

function SubmitDialog({ open, onOpenChange, albums }) {
  const [f, setF] = useState({ url: "", caption: "", album_id: "", consent_publish: false, consent_others: false, allow_public: true });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await cApi.post("/community/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, url: data.url }));
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await cApi.post("/community/gallery/submissions", { ...f, album_id: f.album_id || null });
      toast.success("Thank you! Your photo will appear once approved.");
      setF({ url: "", caption: "", album_id: "", consent_publish: false, consent_others: false, allow_public: true });
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const chk = (k, label) => (
    <label className="flex items-start gap-3 text-sm text-[#4A403B]">
      <input type="checkbox" className="mt-1 accent-[#2B2522]" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`wall-submit-${k}`} />
      <span>{label}</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FBF8F3] max-h-[90vh] overflow-y-auto" data-testid="wall-submit-dialog">
        <DialogTitle className="jc-display text-3xl font-light">Share a memory</DialogTitle>
        <button type="button" onClick={() => ref.current?.click()} className="jc-card-img aspect-[4/3] w-full border border-dashed border-[#C9A97A] flex items-center justify-center" data-testid="wall-submit-upload-btn">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#A8865A]" /> : f.url ? <img src={img(f.url)} alt="" className="w-full h-full object-cover" /> :
            <span className="flex flex-col items-center gap-2 text-[#7A6A62] text-sm"><Upload className="w-6 h-6" strokeWidth={1.25} />Choose a photo</span>}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="wall-submit-file-input" />
        <input className="jc-input" placeholder="Caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="wall-submit-caption" />
        {albums.length > 0 && (
          <select className="jc-input" value={f.album_id} onChange={(e) => setF({ ...f, album_id: e.target.value })} data-testid="wall-submit-album">
            <option value="">Which experience? (optional)</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        )}
        <div className="space-y-3">
          {chk("consent_publish", "I took this photo or have the right to share it, and I consent to Jenova Collective displaying it.")}
          {chk("consent_others", "Everyone recognisable in this photo has agreed to it being shared.")}
          {chk("allow_public", "Show publicly. Untick to show only to signed-in Jenova Girls.")}
        </div>
        <p className="text-xs text-[#7A6A62]">Photos are never published automatically — our team reviews every submission. You can withdraw it anytime from My Jenova.</p>
        <button disabled={!f.url || busy || !f.consent_publish || !f.consent_others} onClick={submit} className="jc-btn jc-btn-primary w-full" data-testid="wall-submit-btn">
          {busy ? "Sending…" : "Submit for approval"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function CommunityWall() {
  const [data, setData] = useState({ photos: [], albums: [], categories: [] });
  const [params, setParams] = useSearchParams();
  const [album, setAlbumState] = useState(params.get("album") || "");
  const setAlbum = (id) => {
    setAlbumState(id);
    setParams(id ? { album: id } : {}, { replace: true });
  };
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [show, setShow] = useState(false);
  const { level, loading: meLoading } = useCommunityMe();

  useEffect(() => {
    const params = {};
    if (album) params.album_id = album;
    if (cat) params.category = cat;
    cApi.get("/community/gallery", { params }).then(({ data }) => setData({ ...data, loaded: true })).catch(() => {});
  }, [album, cat, level]);

  const usedCats = data.categories;
  const currentAlbum = data.albums.find((a) => a.id === album);

  return (
    <CommunityLayout>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionTitle eyebrow="Jenova Community Wall" title="Our girls," em="our memories" testId="wall-title">
            Moments from our experiences — the laughter, the brunches, the sweaty Pilates mornings and the friendships that followed.
          </SectionTitle>
          {level !== "visitor" && (
            <button className="jc-btn jc-btn-blush" onClick={() => setSubmitOpen(true)} data-testid="wall-open-submit-btn">
              <Upload className="w-4 h-4" strokeWidth={1.25} /> Share a photo
            </button>
          )}
        </div>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="wall-albums">
          <button className="jc-chip" data-active={!album} onClick={() => setAlbum("")} data-testid="wall-album-all">All albums</button>
          {data.albums.map((a) => (
            <button key={a.id} className="jc-chip" data-active={album === a.id} onClick={() => setAlbum(a.id)} data-testid={`wall-album-${a.id}`}>{a.title}</button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <select className="jc-chip bg-transparent" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="wall-category-select">
            <option value="">All categories</option>
            {usedCats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {currentAlbum && (
          <p className="mt-6 text-sm text-[#7A6A62]" data-testid="wall-album-info">{fmtDate(currentAlbum.date)}{currentAlbum.description ? ` — ${currentAlbum.description}` : ""}</p>
        )}
        {album && data.loaded && !currentAlbum && level === "visitor" && !meLoading && (
          <div className="mt-6 border border-[#E8D9C4] bg-[#FBF8F3] p-6 max-w-xl" data-testid="wall-album-signin">
            <p className="text-sm text-[#4A403B]">This album is shared with Jenova Girls only. Sign in to see it.</p>
            <button className="jc-btn jc-btn-primary mt-4" onClick={() => startGoogleLogin(`/community-wall?album=${album}`)} data-testid="wall-album-signin-btn">Sign in with Google</button>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {data.photos.length > 1 && (
            <button className="jc-btn jc-btn-ghost mt-6" onClick={() => setShow(true)} data-testid="wall-slideshow-btn">
              <Play className="w-4 h-4" strokeWidth={1.25} /> {currentAlbum ? "Play album slideshow" : "Play slideshow"}
            </button>
          )}
          {currentAlbum && <ShareAlbum album={currentAlbum} />}
        </div>
      </section>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24">
        {data.photos.length ? (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-5 [&>*]:mb-3 md:[&>*]:mb-5" data-testid="wall-grid">
            {data.photos.map((p) => (
              <button key={p.id} onClick={() => setOpen(p)} className="group block w-full break-inside-avoid text-left" data-testid={`wall-photo-${p.id}`}>
                <div className="jc-card-img relative">
                  <img src={img(p.url)} alt={p.caption || ""} className="w-full h-auto" loading="lazy" />
                  {p.featured && <Star className="absolute top-3 right-3 w-4 h-4 text-[#FBF8F3] fill-[#FBF8F3]" />}
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#2B2522]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <p className="text-[#FBF8F3] text-sm line-clamp-2">{p.caption}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : !(album && !currentAlbum && level === "visitor") && (
          <EmptyState title="Memories coming soon" testId="wall-empty">Photos from our first experiences will be shared here.</EmptyState>
        )}
      </section>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl bg-[#FBF8F3] p-0 overflow-hidden" data-testid="wall-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {open && (
            <div>
              <img src={img(open.url)} alt={open.caption || ""} className="w-full max-h-[75vh] object-contain bg-[#2B2522]" />
              <div className="p-6">
                <p className="font-serif text-xl">{open.caption}</p>
                <p className="jc-eyebrow mt-2">{[open.category, fmtDate(open.date)].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} albums={data.albums} />
      {show && <Slideshow photos={data.photos} title={currentAlbum?.title || "Jenova memories"} onClose={() => setShow(false)} />}
    </CommunityLayout>
  );
}
